/**
 * @typedef {Object} ReportAccessRule
 * @property {string} permissionKey Auth permissions key expected on currentUser.permissions.
 * @property {string} getter Vuex getter path used to evaluate final access.
 * @property {Object} [redirectRoute] Named route target used when access is denied.
 * @property {string} redirectRoute.name Nuxt route name.
 * @property {Object} [redirectRoute.params] Optional route params.
 * @property {function(Object): Promise<Object>} [resolveRedirectRoute] Used
 * instead of redirectRoute when the target depends on store state; receives
 * the store and resolves a named route target.
 */

/**
 * @typedef {Object} HydratedUserResult
 * @property {Object|null} currentUser Fully hydrated user object (must include id).
 * @property {boolean} wasHydrated True when middleware had to fetch the user.
 */

/**
 * Report access rules by route name.
 *
 * Keep this map as the single source of truth for:
 * 1. which permission key is required
 * 2. where denied users should be redirected
 *
 * @type {Object<string, ReportAccessRule>}
 */
const REPORT_ACCESS_BY_ROUTE = {
  'dashboard-reports-person': {
    permissionKey: 'peopleReport',
    getter: 'auth/hasPeoplePermission',
    // People splits by history: lapsed subscribers reactivate, the rest subscribe
    resolveRedirectRoute: async store => {
      await store.dispatch('billing/setPeopleSubscriptionHistory');
      return store.getters['billing/peopleSubscriptionRoute'];
    }
  },
  'dashboard-reports-phone': {
    permissionKey: 'phoneReport',
    getter: 'auth/hasPhonePermission',
    redirectRoute: {
      name: 'dashboard-subscribe',
      params: {
        type: 'phone'
      }
    }
  },
  'dashboard-reports-address': {
    permissionKey: 'addressReport',
    getter: 'auth/hasAddressPermission',
    redirectRoute: {
      name: 'dashboard-subscribe',
      params: {
        type: 'address'
      }
    }
  }
};

/**
 * Reads auth user from Vuex.
 *
 * @param {object} store
 * @returns {object}
 */
function getCurrentUser(store) {
  return store.getters['auth/currentUser'] || {};
}

/**
 * Returns true when currentUser looks authenticated.
 *
 * @param {object} currentUser
 * @returns {boolean}
 */
function hasAuthenticatedUser(currentUser) {
  return !!currentUser && Object.keys(currentUser).length > 0;
}

/**
 * Returns true when user object includes id.
 *
 * @param {object} currentUser
 * @returns {boolean}
 */
function hasHydratedUserId(currentUser) {
  return !!currentUser?.id;
}

/**
 * Returns true when permission key has already been loaded into currentUser.
 *
 * @param {object} currentUser
 * @param {string} permissionKey
 * @returns {boolean}
 */
function hasLoadedPermissionKey(currentUser, permissionKey) {
  return (
    !!currentUser.permissions &&
    Object.prototype.hasOwnProperty.call(currentUser.permissions, permissionKey)
  );
}

/**
 * Resolves where a denied user is sent for a rule.
 *
 * @param {ReportAccessRule} rule
 * @param {object} store
 * @returns {Promise<Object>} Named route target.
 */
async function getDeniedRedirectRoute(rule, store) {
  if (rule.resolveRedirectRoute) {
    return rule.resolveRedirectRoute(store);
  }

  return rule.redirectRoute;
}

/**
 * Hydrates currentUser if auth state is partial (permissions-only, no id).
 *
 * @param {object} store
 * @returns {Promise<HydratedUserResult>}
 */
async function ensureHydratedCurrentUser(store) {
  let currentUser = getCurrentUser(store);

  if (!hasAuthenticatedUser(currentUser)) {
    return { currentUser: null, wasHydrated: false };
  }

  if (hasHydratedUserId(currentUser)) {
    return { currentUser, wasHydrated: false };
  }

  // Try to fetch user data to ensure currentUser.id is set
  try {
    await store.dispatch('auth/fetchUser');
  } catch (error) {
    return { currentUser: null, wasHydrated: false };
  }

  currentUser = getCurrentUser(store);

  if (!hasHydratedUserId(currentUser)) {
    return { currentUser: null, wasHydrated: false };
  }

  return { currentUser, wasHydrated: true };
}

/**
 * Dashboard report access middleware.
 *
 * Flow:
 * 1. Load route rule from map; ignore unrelated routes.
 * 2. Ensure currentUser is hydrated (id present).
 * 3. Refresh permissions when needed.
 * 4. Redirect when required getter is false (People resolves its target from
 *    subscription history, so it waits on billing).
 *
 * @param {object} context
 * @param {object} context.route
 * @param {object} context.from
 * @param {object} context.store
 * @param {Function} context.redirect
 * @returns {Promise<void>}
 */
export default async function reportAccessMiddleware({
  route,
  store,
  redirect
}) {
  const rule = REPORT_ACCESS_BY_ROUTE[route.name];

  if (!rule) {
    return;
  }

  const {
    currentUser,
    wasHydrated: userWasHydratedFromPartialState
  } = await ensureHydratedCurrentUser(store);

  // Unauthenticated traffic should be handled by auth guard middleware.
  if (!currentUser) {
    return;
  }

  const shouldRefreshPermissions =
    userWasHydratedFromPartialState ||
    !hasLoadedPermissionKey(currentUser, rule.permissionKey);

  if (shouldRefreshPermissions) {
    const refreshedPermissions = await store.dispatch(
      'auth/checkUserPermissions'
    );

    // If permissions are unresolved, avoid false-negative redirect.
    if (!refreshedPermissions) {
      return;
    }
  }

  if (!store.getters[rule.getter]) {
    redirect(await getDeniedRedirectRoute(rule, store));
    return;
  }
}
